import { createContext, FC, ReactNode, useMemo, useState } from 'react';
import { createTheme, ThemeProvider, useMediaQuery } from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import Cookies from 'js-cookie';
import useThemeCookie from '../hooks/useThemeCookie';
import { getColorTheme } from '../constants/theme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { GMB_CLIENT_ID } from '../shared/constants/constants';
import { GoogleOAuthProvider } from '@react-oauth/google';

interface Props {
  children?: ReactNode;
}

interface SidebarStateProps {
  expanded: boolean;
  setSidebarExpanded: (value: boolean) => void;
}

export const ColorModeContext = createContext({
  mode: 'light',
  toggleColorMode: (mode: string) => {},
});

export const SidebarStateContext = createContext<SidebarStateProps>({
  expanded: true,
  setSidebarExpanded: () => {},
});

const MainProviders: FC<Props> = ({ children }) => {
  const initialSidebarState = localStorage.getItem('expanded') ?? 'true';
  const persistedSidebarState = initialSidebarState === 'true';

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const initialMode = useThemeCookie();

  const [mode, setMode] = useState(initialMode);

  const [expanded, setExpanded] = useState(persistedSidebarState);

  const toggleColorMode = (mode: string) => {
    setMode(() => {
      Cookies.set('theme', mode);
      return mode;
    });
  };

  const getMode = (mode: string) => {
    if (mode === 'light') {
      return 'light';
    } else if (mode === 'dark') {
      return 'dark';
    }
  };

  const currentTheme = prefersDarkMode && mode === 'system' ? 'dark' : getMode(mode);

  const theme = useMemo(() => createTheme(getColorTheme(currentTheme ?? 'light')), [currentTheme]);

  const setSidebarExpanded = () => {
    setExpanded((prevState) => {
      localStorage.setItem('expanded', prevState ? 'false' : 'true');
      return !prevState;
    });
  };

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <StyledThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SidebarStateContext.Provider value={{ expanded, setSidebarExpanded }}>
              <GoogleOAuthProvider clientId={GMB_CLIENT_ID}>{children}</GoogleOAuthProvider>
            </SidebarStateContext.Provider>
          </LocalizationProvider>
        </StyledThemeProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default MainProviders;
